import {useRef, useState} from 'react'
import {AnchoredOverlay, BranchName, Button, IconButton, NavList, useResponsiveValue} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {PlusIcon, StackIcon, TrashIcon, XIcon, GitBranchIcon} from '@primer/octicons-react'
import {getBaseBranchText} from '../utils/branch-label'
import type {StackData} from '../page-data/payloads/header'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useUnstackPullRequestsMutation} from '../mutations/use-unstack-pull-requests-mutation'
import {useQueryClient} from '@github-ui/react-query'
import {useHeaderPageDataQueryKey} from '../page-data/loaders/use-header-page-data'
import {useLocation} from '@github-ui/react-router'
import {getPullRequestTabSuffix} from '@github-ui/paths'
import {UnstackDialog} from './UnstackButtonDialog'
import {StateIcon} from './StateIcon'
import styles from './StackState.module.css'

type StackStateProps = {
  pullRequestNumber: number
  stack: StackData | undefined | null
}

export function StackState({pullRequestNumber, stack}: StackStateProps) {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isPullRequestStacksEnabled = useFeatureFlag('pull_request_stacks')
  const [open, setOpen] = useState<boolean>(false)
  const [isConfirmingUnstack, setIsConfirmingUnstack] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState('')
  const unstackButtonRef = useRef<HTMLButtonElement>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const queryClient = useQueryClient()
  const headerPageDataQueryKey = useHeaderPageDataQueryKey()
  const {mutate: mutateUnstackPullRequests, isPending: isLoading} = useUnstackPullRequestsMutation()
  const {pathname} = useLocation()
  const isNarrow = useResponsiveValue({narrow: true}, false)

  if (!isPullRequestStacksEnabled) {
    return null
  }

  if (!stack) {
    return null
  }

  const {baseBranch, repositoryOwnerLogin, repositoryName, position, size, pulls} = stack
  const baseBranchText = getBaseBranchText(baseBranch, repositoryOwnerLogin, repositoryOwnerLogin)

  // Pulls array is ordered from head to base, so top pull in the stack is the first item in the array.
  const topPullInStack = pulls[0]
  const addToStackBaseBranch = topPullInStack?.headBranch ?? baseBranch
  const addToStackHeadBranch = addToStackBaseBranch // Use same branch for now, until we have a "smarter" way of determining head ref for new PR
  const addToStackURL = `/${repositoryOwnerLogin}/${repositoryName}/compare/${addToStackBaseBranch}...${addToStackHeadBranch}?stack=true`

  // Preserve the current tab (e.g. /changes, /commits) when navigating between stacked PRs
  const tabSuffix = getPullRequestTabSuffix(pathname)

  const stackNavContent = (
    <NavList className={styles.stackNavList}>
      <NavList.Item href={addToStackURL} className="d-flex align-items-center">
        <NavList.LeadingVisual className="py-1">
          <PlusIcon />
        </NavList.LeadingVisual>
        <div>Add to stack</div>
      </NavList.Item>
      {pulls.map(stackedPR => {
        const isCurrent = stackedPR.number === pullRequestNumber
        return (
          <NavList.Item
            key={stackedPR.number}
            href={`${stackedPR.url}${tabSuffix}`}
            aria-current={isCurrent ? 'page' : undefined}
          >
            <NavList.LeadingVisual>
              <StateIcon state={stackedPR.state} />
            </NavList.LeadingVisual>
            <div className={styles.prItemContent}>
              <span className={styles.prTitle}>{stackedPR.title}</span>
              <span className={styles.prMeta}>
                <span className={'text-normal'}>#{stackedPR.number}</span>
                <span className={styles.prBranch}>
                  <BranchName as="span" className={styles.prBranchName}>
                    {stackedPR.headBranch}
                  </BranchName>
                </span>
              </span>
            </div>
          </NavList.Item>
        )
      })}
      <li className={styles.branchItem}>
        <span className={styles.branchItemContent}>
          <GitBranchIcon className="fgColor-muted" />
          <BranchName as="span">{baseBranchText}</BranchName>
        </span>
      </li>
    </NavList>
  )

  return (
    <div className="d-flex flex-items-center fgColor-muted">
      <Button
        ref={anchorRef}
        className={styles.stackBadge}
        variant="invisible"
        leadingVisual={StackIcon}
        aria-label={`Navigate pull request stack, position ${position} of ${size}`}
        aria-expanded={open}
        aria-haspopup={isNarrow ? 'dialog' : 'true'}
        onClick={() => setOpen(!open)}
      >
        {position}/{size}
      </Button>
      {isNarrow ? (
        <>
          {open && (
            <Dialog
              renderHeader={({dialogLabelId, onClose: closeDialog}) => (
                <div className={styles.dialogHeader}>
                  <Dialog.Title id={dialogLabelId}>Stack</Dialog.Title>
                  <div className={styles.overlayHeaderActions}>
                    <IconButton
                      ref={unstackButtonRef}
                      icon={TrashIcon}
                      variant="invisible"
                      size="small"
                      aria-label="Unstack"
                      onClick={() => setIsConfirmingUnstack(true)}
                    />
                    <IconButton
                      icon={XIcon}
                      variant="invisible"
                      size="small"
                      aria-label="Close"
                      onClick={() => closeDialog('close-button')}
                    />
                  </div>
                </div>
              )}
              onClose={() => setOpen(false)}
              position={{narrow: 'bottom', regular: 'center'}}
              height="auto"
              renderBody={({children}) => <Dialog.Body className="px-1 pt-1 pb-2">{children}</Dialog.Body>}
            >
              {stackNavContent}
            </Dialog>
          )}
        </>
      ) : (
        <AnchoredOverlay
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          renderAnchor={null}
          anchorRef={anchorRef}
        >
          <div>
            <div className={styles.overlayHeader}>
              <span className={styles.overlayHeaderTitle}>Stack</span>
              <div className={styles.overlayHeaderActions}>
                <IconButton
                  ref={unstackButtonRef}
                  icon={TrashIcon}
                  variant="invisible"
                  size="small"
                  aria-label="Unstack"
                  onClick={() => setIsConfirmingUnstack(true)}
                />
                <IconButton
                  icon={XIcon}
                  variant="invisible"
                  size="small"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                />
              </div>
            </div>
            {stackNavContent}
          </div>
        </AnchoredOverlay>
      )}
      <UnstackDialog
        unstackButtonRef={unstackButtonRef}
        isConfirmingUnstack={isConfirmingUnstack}
        isInactive={stack.pulls.some(pull => !pull.isUnstackable)}
        isLoading={isLoading}
        errorMessage={errorMessage}
        onCancelUnstack={() => {
          setIsConfirmingUnstack(false)
          setErrorMessage('')
        }}
        onConfirmUnstack={() => {
          setErrorMessage('')
          mutateUnstackPullRequests(
            {stackId: stack.id},
            {
              onError: error => setErrorMessage(error.message),
              onSuccess: async () => {
                try {
                  await queryClient.refetchQueries({queryKey: headerPageDataQueryKey}, {throwOnError: true})
                  setIsConfirmingUnstack(false)
                  setOpen(false)
                } catch {
                  setErrorMessage('Something unexpected happened. Please refresh the page.')
                }
              },
            },
          )
        }}
      />
    </div>
  )
}
